// © Microsoft Corporation. All rights reserved.

import { AppType, SignedInStatus } from "@msnews/experiences-constants";
import { KeyValueArray, KeyValuePair, appEnvironment, getAppEnvironment, getFromWebWorker, isWebWorkerPresent } from "@msnews/core";
import { OneServiceUtility, getServiceUrlForSubpath, sendRequest } from "@msnews/oneservice";
import { PdpDataContract, SegmentCardType, TargetType } from "@msnews/pdp-service-contracts";
import { PdpUserDataContract, SegmentCardTypeEnabledStatus } from "./PdpUserDataContract";
import { UnknownBulkLoadGraphActionType, logger } from "@msnews/diagnostics";

import { Telemetry } from "@msnews/telemetry-contracts";
import { WebWorkerMessageId } from "@msnews/web-worker";
import { canUseDOM } from "@msnews/isomorphic-util";
import { getUserSignInState } from "@msnews/auth";

/**
 * A class that provides service wrappers for PDP Service Client
 *
 * @export
 */
export abstract class PdpServiceClient {
    /**
     * the userUpdate Endpoint
     */
    private userUpdateEndpoint = "msn/user";
    /**
     * the pdp service end point
     */
    private pdpServiceEndpoint = "graph/actions";

    /**
     * Promise to fetch results from the bulk graph/action preload call. This is used
     * to synchronize multiple requests coming to fetch disparate target types under the
     * same session.
     */
    private bulkGraphActionPromise: Promise<Map<TargetType, PdpDataContract[]>>;

    /**
     * The fetched results from the bulk graph action call. This will act as cache that can
     * be invalidated per target type as desired so subsequent calls will go to network.
     */
    private bulkGraphActionResultCache: Map<TargetType, PdpDataContract[]>;

    /**
     * List of target types to bulk load via graph/actions. This is normally set at the
     * page/entry-point level according to the configured experiences on the page so that
     * when the first graph/action call comes in, this will be used to fetch that list in bulk,
     * and any subsequent results should get served from that single action.
     */
    private bulkGraphActionTargetTypes: TargetType[];

    /**
     * the ocid for request needs to be camel cased
     */
    protected ocid = "Peregrine";

    /**
     * adding page info to the request
     */
    protected ocidOverride = false;

    /**
     * Initializes a new instance of the PDP Service Client
     * @constructor
     * @public
     */
    protected constructor(private fetchImpl: (input: RequestInfo, init?: RequestInit) => Promise<Response>) {
    }

    /**
     * add new favorite location
     *
     * @returns {Promise<boolean>} success status
     *
     */
    public async addToFavoriteLocation(metaData: any, definitionName: string): Promise<boolean> {
        const degree = "FavoriteLocation";
        const targetType = "Location";
        const actionType = "Follow";
        const requestParams: RequestInit = {
            method: "POST",
            body: JSON.stringify({
                actionType,
                targetType,
                definitionName,
                degree,
                metaData
            }),
            headers: await OneServiceUtility.getOneServiceHeaders()
        };
        requestParams.credentials = "include";
        const serviceUrl = getServiceUrlForSubpath(this.pdpServiceEndpoint);
        const userIsSignedIn: boolean = await getUserSignInState() === SignedInStatus.SignedIn;
        const commonParams: KeyValueArray<string, string> = userIsSignedIn ?
            OneServiceUtility.getOneServiceParamsWithAuth(getAppEnvironment().UserId, this.ocid, !this.ocidOverride) :
            OneServiceUtility.getOneServiceParamsWithoutAuth(getAppEnvironment().UserId, this.ocid, !this.ocidOverride);

        commonParams.forEach((pair) => {
            if (pair.value) {
                serviceUrl.searchParams.set(pair.key, pair.value);
            }
        });

        let postResponse: any;
        try {
            postResponse = await (sendRequest(
                async () => {
                    const innerResponse: Response = await this.fetchImpl(serviceUrl.href, requestParams);
                    if (innerResponse.status >= 400) {
                        const errMsg = `OneService returned error response. Status: ${innerResponse.status} for actionType: ${actionType}, targetType: ${targetType}`;
                        throw new Error(errMsg);
                    }
                    return innerResponse.json();
                },
                "addToFavoriteLocation"
            ));
        } catch (error) {
            return false;
        }
        return true;
    }

    public async removeFavoriteLocation(definitionName: string): Promise<boolean> {
        return await this.deleteFromPdp(TargetType.Location, "definitionName", [definitionName]);
    }

    /**
     * Updates the user Home Location
     * Error if udpate failure and need to catch out of function
     * @returns {Promise<boolean>} success status
     */
    public async updatePdpHomeLocation(metaData: any, definitionName: string, ocidOverride?: string): Promise<boolean> {
        const degree = "DefaultLocation";
        const targetType = TargetType.Location;
        const actionType = "Follow";
        const requestParams: RequestInit = {
            method: "POST",
            body: JSON.stringify({
                actionType,
                targetType,
                definitionName,
                degree,
                metaData
            }),
            headers: await OneServiceUtility.getOneServiceHeaders()
        };
        requestParams.credentials = "include";
        const serviceUrl = getServiceUrlForSubpath(this.pdpServiceEndpoint);
        const userIsSignedIn: boolean = await getUserSignInState() === SignedInStatus.SignedIn;
        const ocid = ocidOverride ? ocidOverride : this.ocid;
        // if we have ocidOverride, then do not encode page info
        const commonParams: KeyValueArray<string, string> = userIsSignedIn ?
            OneServiceUtility.getOneServiceParamsWithAuth(getAppEnvironment().UserId, ocid, !ocidOverride) :
            OneServiceUtility.getOneServiceParamsWithoutAuth(getAppEnvironment().UserId, ocid, !ocidOverride);

        commonParams.forEach((pair) => {
            if (pair.value) {
                serviceUrl.searchParams.set(pair.key, pair.value);
            }
        });

        await sendRequest(
            async () => {
                const innerResponse = await this.fetchImpl(serviceUrl.href, requestParams);
                if (innerResponse.status >= 400) {
                    const errMsg = `OneService returned error response. Status: ${innerResponse.status} for actionType: ${actionType}, targetType: ${targetType}`;
                    throw new Error(errMsg);
                }
                return innerResponse;
            },
            "setPdpHomeLocation"
        );

        // now that we've updated the location, invalidate that entry from cache if applicable
        this.tryInvalidateBulkFetchedPdpTargetType(targetType);
        return true;
    }

    /**
     * Updates the user commute home or work location
     * @returns {Promise<boolean>} success status
     */
    public async updateCommuteLocation(isHome: boolean, location: string, formattedAddress: string): Promise<boolean> {
        const targetType = TargetType.Traffic;
        const actionType = "Follow";
        const requestParams: RequestInit = {
            method: "POST",
            body: JSON.stringify({
                actionType,
                targetType,
                degree: isHome ? "HomeLocation" : "WorkLocation",
                definitionName: location,
                metadata: {
                    formattedAddress
                }
            }),
            headers: await OneServiceUtility.getOneServiceHeaders()
        };
        requestParams.credentials = "include";
        const serviceUrl = getServiceUrlForSubpath(this.pdpServiceEndpoint);
        const userIsSignedIn: boolean = await getUserSignInState() === SignedInStatus.SignedIn;
        const commonParams: KeyValueArray<string, string> = userIsSignedIn ?
            OneServiceUtility.getOneServiceParamsWithAuth(getAppEnvironment().UserId, this.ocid, !this.ocidOverride) :
            OneServiceUtility.getOneServiceParamsWithoutAuth(getAppEnvironment().UserId, this.ocid, !this.ocidOverride);

        commonParams.forEach((pair) => {
            if (pair.value) {
                serviceUrl.searchParams.set(pair.key, pair.value);
            }
        });

        let postResponse: any;
        try {
            postResponse = await (sendRequest(
                async () => {
                    const innerResponse: Response = await this.fetchImpl(serviceUrl.href, requestParams);
                    if (innerResponse.status >= 400) {
                        const errMsg = `OneService returned error response. Status: ${innerResponse.status} for actionType: ${actionType}, targetType: ${targetType}`;
                        throw new Error(errMsg);
                    }
                    return innerResponse.json();
                },
                "updateCommuteLocation"
            ));

            // now that we've updated the target type, invalidate that entry from cache if applicable
            this.tryInvalidateBulkFetchedPdpTargetType(targetType);

        } catch (error) {
            return false;
        }
        return true;
    }

    /**
     * Add a quote to user watch list
     *
     * @returns {Promise<boolean>} success status
     *
     */
    public async addToWatchList(quoteId: string, securityType = 0): Promise<boolean> {
        const targetType = TargetType.Finance;
        const actionType = "Follow";
        const requestParams: RequestInit = {
            method: "POST",
            body: JSON.stringify({
                actionType,
                targetType,
                targetId: quoteId,
                metadata: {
                    securityType: securityType,
                    tickerId: this.generateUUID()
                }
            }),
            headers: await OneServiceUtility.getOneServiceHeaders()
        };
        requestParams.credentials = "include";
        const serviceUrl = getServiceUrlForSubpath(this.pdpServiceEndpoint);
        const userIsSignedIn: boolean = await getUserSignInState() === SignedInStatus.SignedIn;
        const commonParams: KeyValueArray<string, string> = userIsSignedIn ?
            OneServiceUtility.getOneServiceParamsWithAuth(getAppEnvironment().UserId, this.ocid, !this.ocidOverride) :
            OneServiceUtility.getOneServiceParamsWithoutAuth(getAppEnvironment().UserId, this.ocid, !this.ocidOverride);

        commonParams.forEach((pair) => {
            if (pair.value) {
                serviceUrl.searchParams.set(pair.key, pair.value);
            }
        });

        let postResponse: any;
        try {
            postResponse = await (sendRequest(
                async () => {
                    const innerResponse: Response = await this.fetchImpl(serviceUrl.href, requestParams);
                    if (innerResponse.status >= 400) {
                        const errMsg = `OneService returned error response. Status: ${innerResponse.status} for actionType: ${actionType}, targetType: ${targetType}`;
                        throw new Error(errMsg);
                    }
                    return innerResponse.json();
                },
                "addToWatchList"
            ));

            // now that we've updated the target type, invalidate that entry from cache if applicable
            this.tryInvalidateBulkFetchedPdpTargetType(targetType);

        } catch (error) {
            return false;
        }
        return true;
    }

    private generateUUID(): string {
        let dt = new Date().getTime();
        return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function (c) {
            const r = (dt + Math.random() * 16) % 16 | 0;
            dt = Math.floor(dt / 16);
            return (c == "x" ? r : (r & 0x3 | 0x8)).toString(16);
        });
    }

    /**
     * Sort user watch list.
     *
     * @returns {Promise<boolean>} success status
     *
     */
    public async sortWatchList(ids: string[]): Promise<boolean> {
        const targetType = TargetType.Finance;
        const pdpWatchlist = await pdpServiceClient.readPdpWithTargetType(TargetType.Finance, true, 200);
        const watchlist = pdpWatchlist && pdpWatchlist.value;
        if (watchlist.length != ids.length) {
            return false;
        }
        const actionType = "Follow";
        const body = {};
        ids.forEach((id, index) => {
            const quote = watchlist.filter(q => q.targetId === id)[0];
            if (!quote) {
                return false;
            }
            body[id] = JSON.stringify({
                actionType: "Follow",
                targetId: id,
                targetType: targetType,
                rank: index,
                metadata: quote.metadata
            });
        });
        const requestParams: RequestInit = {
            method: "PUT",
            body: JSON.stringify(body),
            headers: await OneServiceUtility.getOneServiceHeaders()
        };
        requestParams.credentials = "include";
        const serviceUrl = getServiceUrlForSubpath(this.pdpServiceEndpoint);
        const userIsSignedIn: boolean = await getUserSignInState() === SignedInStatus.SignedIn;
        const commonParams: KeyValueArray<string, string> = userIsSignedIn ?
            OneServiceUtility.getOneServiceParamsWithAuth(getAppEnvironment().UserId, this.ocid, !this.ocidOverride) :
            OneServiceUtility.getOneServiceParamsWithoutAuth(getAppEnvironment().UserId, this.ocid, !this.ocidOverride);

        commonParams.forEach((pair) => {
            if (pair.value) {
                serviceUrl.searchParams.set(pair.key, pair.value);
            }
        });

        try {
            await (sendRequest(
                async () => {
                    const innerResponse: Response = await this.fetchImpl(serviceUrl.href, requestParams);
                    if (innerResponse.status >= 400) {
                        const errMsg = `OneService returned error response. Status: ${innerResponse.status} for actionType: ${actionType}, targetType: ${targetType}`;
                        throw new Error(errMsg);
                    }
                    return innerResponse.json();
                },
                "sortWatchList"
            ));
            // now that we've updated the target type, invalidate that entry from cache if applicable
            this.tryInvalidateBulkFetchedPdpTargetType(TargetType.Finance);
        } catch (error) {
            return false;
        }
        return true;
    }

    /**
     * Updates the user's Weather preference settings such as unit and location mode (specify/detect)
     * Error if udpate failure and need to catch out of function
     * @returns {Promise<boolean>} success status
     */
    public async updateWeatherSettings(newUnit: string, newLocMode: string, ocidOverride?: string): Promise<boolean> {
        if (!newUnit && !newLocMode) {
            return true;
        }

        let dummyid = "";
        if (newUnit && !newLocMode) {
            dummyid = "{\"weatherDisplayUnit\":\"" + newUnit + "\"}";
        } else if (!newUnit && newLocMode) {
            dummyid = "{\"userSettings\":{\"weatherLocationSettingMode\":\"" + newLocMode + "\"}}";
        } else {
            dummyid = "{\"weatherDisplayUnit\":\"" + newUnit + "\",\"userSettings\":{\"weatherLocationSettingMode\":\"" + newLocMode + "\"}}";
        }
        const requestParams: RequestInit = {
            method: "POST",
            body: dummyid,
            headers: await OneServiceUtility.getOneServiceHeaders()
        };
        requestParams.credentials = "include";
        const serviceUrl = getServiceUrlForSubpath(this.userUpdateEndpoint);
        const userIsSignedIn: boolean = await getUserSignInState() === SignedInStatus.SignedIn;
        const ocid = ocidOverride ? ocidOverride : this.ocid;
        // if we have ocidOverride, then do not encode page info
        const commonParams: KeyValueArray<string, string> = userIsSignedIn ?
            OneServiceUtility.getOneServiceParamsWithAuth(getAppEnvironment().UserId, ocid, !ocidOverride) :
            OneServiceUtility.getOneServiceParamsWithoutAuth(getAppEnvironment().UserId, ocid, !ocidOverride);

        commonParams.forEach((pair) => {
            if (pair.value) {
                serviceUrl.searchParams.set(pair.key, pair.value);
            }
        });

        const innerResponse: Response = await this.fetchImpl(serviceUrl.href, requestParams);
        if (innerResponse.status >= 400) {
            const errMsg = `OneService returned error response. Status: ${innerResponse.status} for update weather setting call`;
            throw new Error(errMsg);
        }

        return true;
    }

    /**
     * Gets the user's saved preference from One Service
     * @returns {Promise<PdpUserDataContract[]>} returns the saved preference
     */
    public async getPdpUserPreference(): Promise<{ value: PdpUserDataContract[] }> {
        const requestParams: RequestInit = {
            method: "GET",
            credentials: "include",
            headers: await OneServiceUtility.getOneServiceHeaders()
        };

        const serviceUrl = getServiceUrlForSubpath(this.userUpdateEndpoint);
        const userIsSignedIn: boolean = await getUserSignInState() === SignedInStatus.SignedIn;
        const commonParams: KeyValueArray<string, string> = userIsSignedIn
            ? OneServiceUtility.getOneServiceParamsWithAuth(getAppEnvironment().UserId, this.ocid, !this.ocidOverride)
            : OneServiceUtility.getOneServiceParamsWithoutAuth(getAppEnvironment().UserId, this.ocid, !this.ocidOverride);

        [...commonParams].forEach((pair) => {
            if (pair.value) {
                serviceUrl.searchParams.set(pair.key, pair.value);
            }
        });

        try {
            const response = await (sendRequest(
                async () => {
                    const response: Response = await this.fetchImpl(serviceUrl.href, requestParams);
                    if (!response.ok) {
                        throw Error(response.statusText);
                    }
                    return response;
                },
                "readPDPData"));

            return await response.json();
        } catch (error) {
            // Caller will handle when the response is not valid, but still logging the error
            logger.logError(error.message);
            return null;
        }
    }

    /**
     * Updates the user's saved layout preference in One Service
     * @param layout User's new selected layout
     * @returns {Promise<boolean>} returns success status
     */
    public async updatePdpLayoutPreference(layout: string): Promise<boolean> {
        const requestParams: RequestInit = {
            method: "PUT",
            credentials: "include",
            body: JSON.stringify({
                dummyid: { userLayoutPreference: layout }
            })
        };
        const serviceUrl = getServiceUrlForSubpath(this.userUpdateEndpoint);
        const userIsSignedIn: boolean = await getUserSignInState() === SignedInStatus.SignedIn;
        const commonParams: KeyValueArray<string, string> = userIsSignedIn ?
            OneServiceUtility.getOneServiceParamsWithAuth(getAppEnvironment().UserId, this.ocid, !this.ocidOverride) :
            OneServiceUtility.getOneServiceParamsWithoutAuth(getAppEnvironment().UserId, this.ocid, !this.ocidOverride);

        commonParams.forEach((pair) => {
            if (pair.value) {
                serviceUrl.searchParams.set(pair.key, pair.value);
            }
        });

        try {
            const response: Response = await this.fetchImpl(serviceUrl.href, requestParams);
            if (response.status >= 400) {
                const errMsg = `OneService returned error response. Status: ${response.status} for update layout preference setting call`;
                throw new Error(errMsg);
            }

        } catch (error) {
            // Caller will handle when the response is not valid, but still logging the error
            logger.logError(error.message);
            return false;
        }
        return true;
    }

    /**
     * Update segment cards enable status
     * @returns {Promise<boolean>} success status
     */
    public async updateSegmentCardsEnableStatus(segmentCards: SegmentCardTypeEnabledStatus[]): Promise<boolean> {
        if (!segmentCards && !segmentCards.length) {
            return true;
        }

        const updateCardStatusCont = segmentCards.map(
            segmentCardType => `\\"${segmentCardType.cardType}\\":${segmentCardType.enabled ? "\\\"1\\\"" : "\\\"0\\\""}`
        ).join(",");
        const payload = "{\"segmentCardSettings\":\"{" + updateCardStatusCont + "}\"}";
        const requestParams: RequestInit = {
            method: "POST",
            body: payload
        };
        requestParams.credentials = "include";
        const serviceUrl = getServiceUrlForSubpath(this.userUpdateEndpoint);
        const userIsSignedIn: boolean = await getUserSignInState() === SignedInStatus.SignedIn;
        const commonParams: KeyValueArray<string, string> = userIsSignedIn ?
            OneServiceUtility.getOneServiceParamsWithAuth(getAppEnvironment().UserId, this.ocid, !this.ocidOverride) :
            OneServiceUtility.getOneServiceParamsWithoutAuth(getAppEnvironment().UserId, this.ocid, !this.ocidOverride);

        commonParams.forEach((pair) => {
            if (pair.value) {
                serviceUrl.searchParams.set(pair.key, pair.value);
            }
        });

        try {
            const innerResponse: Response = await this.fetchImpl(serviceUrl.href, requestParams);
            if (innerResponse.status >= 400) {
                const errMsg = `OneService returned error response. Status: ${innerResponse.status} for run update ${updateCardStatusCont} call`;
                throw new Error(errMsg);
            }
        } catch (error) {
            return false;
        }
        return true;
    }

    /**
     * Set the list of graph/action target types to bulk fetch for the page.
     * @param targetTypes - the list of target types to bulk fetch for the page.
     */
    public setPdpTargetTypesForBulkLoad(targetTypes: TargetType[]): void {
        // should only set it once when a valid list is passed
        if (targetTypes && targetTypes.length && !this.bulkGraphActionTargetTypes) {
            this.bulkGraphActionTargetTypes = targetTypes;
        }
    }

    /**
     * Fetch the result from graph/action for the given target type. It will optionally try
     * to load the sub-result via a bulk fetch call to graph/action if so configured on the page.
     * @param targetType - the target type of the graph/action call
     * @returns {Promise} that returns the value of the PDP fetch call.
     */
    public async readPdpWithTargetType(targetType: TargetType, noStore = false, top = undefined): Promise<{ value: PdpDataContract[] }> {

        const pdpData = await this.getFromWebWorker(targetType);
        if (pdpData) {
            return pdpData;
        }

        if (this.bulkGraphActionTargetTypes
            && this.bulkGraphActionTargetTypes.length) {

            // only proceed to bulk fetch if that incoming target type matches one in the bulk target list
            if (this.bulkGraphActionTargetTypes.indexOf(targetType) >= 0) {

                // try to assign the cache if cached result hasn't been assigned yet, else await the cached promise.
                this.bulkGraphActionResultCache = this.bulkGraphActionResultCache
                    // if we haven't created the bulk fetch promise, create it now, so that future callers will get served through this promise.
                    || await (this.bulkGraphActionPromise || (this.bulkGraphActionPromise = this.fetchPdpWithTargetTypes(this.bulkGraphActionTargetTypes, noStore, top)));

                // wait for bulk call to finish, and then return the result if it exists there.
                // For subsequent callers this await is largely a no-op as actual async work would've finished already.
                const value = this.bulkGraphActionResultCache.get(targetType);
                if (value) {
                    return { value };
                }

            } else {

                // if we received a target type that was not in the configured bulk target types list, report an error
                // so someone can go update the config for the page to include this new page type.
                Telemetry.sendAppErrorEvent({
                    ...UnknownBulkLoadGraphActionType,
                    message: `Received an unknown target type "${targetType}" that was not in the bulk graph action target types list "${this.bulkGraphActionTargetTypes}". Falling back to new network call. Please update the EntryPoint config's "graphActionsToBulkLoad" property to include this type.`
                });
            }
        }

        // if we still don't have a result from web-worker cache, or from the bulk request configured on the page,
        // do the individual call.
        const value = (await this.fetchPdpWithTargetTypes([targetType], noStore, top)).get(targetType);
        if (value) {
            return { value };
        }
    }

    /**
     * Delete from graph/action for the given target type by targetId.
     * @param targetType - the target type of the graph/action call
     * @returns {Promise} that returns the value of the PDP fetch call.
     */
    public async deleteFromPdp(targetType: TargetType, targetIdFieldName: string, ids: string[]): Promise<boolean> {
        const requestParams: RequestInit = {
            method: "DELETE",
            headers: await OneServiceUtility.getOneServiceHeaders()
        };
        requestParams.credentials = "include";
        const serviceUrl = getServiceUrlForSubpath(this.pdpServiceEndpoint);
        const userIsSignedIn: boolean = await getUserSignInState() === SignedInStatus.SignedIn;
        const commonParams: KeyValueArray<string, string> = userIsSignedIn ?
            OneServiceUtility.getOneServiceParamsWithAuth(getAppEnvironment().UserId, this.ocid, !this.ocidOverride) :
            OneServiceUtility.getOneServiceParamsWithoutAuth(getAppEnvironment().UserId, this.ocid, !this.ocidOverride);
        const targetTypeFilter = `targetType eq '${targetType}'`;
        const idQuery = ids.map(id => `${targetIdFieldName} eq '${id}'`).join(" or ");
        serviceUrl.searchParams.set("$filter", `actionType eq 'Follow' and (${idQuery}) and (${targetTypeFilter})`);
        commonParams.forEach((pair) => {
            if (pair.value) {
                serviceUrl.searchParams.set(pair.key, pair.value);
            }
        });

        try {
            await (sendRequest(
                async () => {
                    const innerResponse: Response = await this.fetchImpl(serviceUrl.href, requestParams);
                    if (innerResponse.status >= 400) {
                        const errMsg = `OneService returned error response. Status: ${innerResponse.status} for actionType: Follow, targetType: ${targetType}`;
                        throw new Error(errMsg);
                    }
                    return innerResponse;
                },
                "deleteFromPdp"
            ));
            // now that we've updated the target type, invalidate that entry from cache if applicable
            this.tryInvalidateBulkFetchedPdpTargetType(targetType);
        } catch (error) {
            return false;
        }
        return true;
    }

    /**
     * Helper to invalidate a previously bulk fetched PDP target type result from cache.
     * @param type - the target type to evict from cache.
     */
    private tryInvalidateBulkFetchedPdpTargetType(type: TargetType): void {
        if (this.bulkGraphActionResultCache) {
            this.bulkGraphActionResultCache.delete(type);
        }
    }

    /**
     * Read PDP Data for the given list of target types directly from network.
     * @returns {Promise<any>} returns bulk PDP response grouped by target type.
     */
    private async fetchPdpWithTargetTypes(targetTypes: TargetType[], noStore = false, top = undefined): Promise<Map<TargetType, PdpDataContract[]>> {

        if (!targetTypes.length) {
            return new Map();
        }

        const targetTypeFilter = targetTypes
            .map(targetType => `targetType eq '${targetType}'`)
            .join(" or ");
        top = top || targetTypes.length * 10 * 2;

        const pdpReadParams: Array<KeyValuePair<string, string>> = [
            // 10 is the default length returned by the graph/actions, * 2 is added factor since
            // we can't guarantee that 10 of each type requested will be returned.
            { key: "$top", value: `${top}` },
            { key: "$filter", value: `actionType eq 'Follow' and (${targetTypeFilter})` }
        ];

        const requestParams = await OneServiceUtility.getBaseRequestData("GET");
        requestParams.credentials = "include";
        if (noStore === true) {
            requestParams.cache = "no-store";
        }

        const serviceUrl = getServiceUrlForSubpath(this.pdpServiceEndpoint);
        const userIsSignedIn: boolean = await getUserSignInState() === SignedInStatus.SignedIn;
        const commonParams: KeyValueArray<string, string> = userIsSignedIn
            ? OneServiceUtility.getOneServiceParamsWithAuth(getAppEnvironment().UserId, this.ocid, !this.ocidOverride)
            : OneServiceUtility.getOneServiceParamsWithoutAuth(getAppEnvironment().UserId, this.ocid, !this.ocidOverride);

        [...pdpReadParams, ...commonParams].forEach((pair) => {
            if (pair.value) {
                serviceUrl.searchParams.set(pair.key, pair.value);
            }
        });

        const response = await (sendRequest(
            async () => {
                const response = await this.fetchImpl(serviceUrl.href, requestParams);
                if (!response.ok) {
                    throw Error(response.statusText);
                }
                return response;
            },
            "readPDPData"));

        const results = new Map<TargetType, PdpDataContract[]>();

        // if network fetch returned valid data, aggregate them under the map
        const pdpData: { value: PdpDataContract[] } = await response.json();
        if (!pdpData) {
            return results;
        }

        if (pdpData.value) {
            for (const value of pdpData.value) {
                let list = results.get(value.targetType);
                if (!list) {
                    results.set(value.targetType, list = []);
                }
                list.push(value);
            }
        }

        // make sure to fill in the missing type values, and add an empty array for any missing
        // type result to indicate that we fetched it, but didn't have valid results so that
        // we don't try to fetch from network again.
        for (const targetType of targetTypes) {
            if (!results.get(targetType)) {
                results.set(targetType, []);
            }
        }

        return results;
    }

    /**
     * Updates user settings with preferred market
     * @param market selected market
     */
    public async updateMarketPreference(market: string): Promise<boolean> {
        const requestParams: RequestInit = {
            method: "POST",
            credentials: "include",
            body: JSON.stringify({ userSettings: { preferredMarket: market } })
        };

        const serviceUrl = getServiceUrlForSubpath(this.userUpdateEndpoint);
        const userIsSignedIn: boolean = await getUserSignInState() === SignedInStatus.SignedIn;
        const commonParams: KeyValueArray<string, string> = userIsSignedIn ?
            OneServiceUtility.getOneServiceParamsWithAuth(getAppEnvironment().UserId, this.ocid, !this.ocidOverride) :
            OneServiceUtility.getOneServiceParamsWithoutAuth(getAppEnvironment().UserId, this.ocid, !this.ocidOverride);

        commonParams.forEach((pair) => {
            if (pair.value) {
                serviceUrl.searchParams.set(pair.key, pair.value);
            }
        });

        try {
            const response: Response = await this.fetchImpl(serviceUrl.href, requestParams);
            if (!response.ok) {
                throw new Error(`Failed to update market preference: Status: ${response.status}`);
            }

            return true;

        } catch (error) {
            // Caller will handle when the response is not valid, but still logging the error
            logger.logError(error.message);
            return false;
        }
    }

    /**
     * Try reading from webworker
     *
     * @returns {Promise<any>} returns pdp response
     *
     */
    private async getFromWebWorker(targetType: TargetType): Promise<any> {
        let pdpDataFromWebWorker: any = null;
        if (isWebWorkerPresent) {
            const webworkerId = this.maptoWebWorkerId(targetType);
            if (webworkerId && webworkerId.trim()) {
                const response = await getFromWebWorker({ id: this.maptoWebWorkerId(targetType) });
                if (response.fetched) {
                    pdpDataFromWebWorker = response.payload;
                }
            }
        }
        return pdpDataFromWebWorker;
    }

    /**
     * Map to web worker Id
     *
     * @returns {string} returns webworker Id
     */
    private maptoWebWorkerId(targetType: TargetType): string {
        switch (targetType) {
            case TargetType.Location: {
                return WebWorkerMessageId.WeatherPdp;
            }
            case TargetType.League: {
                return WebWorkerMessageId.SportsLeaguePdp;
            }
            case TargetType.Player: {
                return WebWorkerMessageId.SportsPlayerPdp;
            }
            case TargetType.Team: {
                return WebWorkerMessageId.SportsTeamPdp;
            }
            case TargetType.Finance: {
                return WebWorkerMessageId.MoneyPdp;
            }
            default: {
                return "";
            }

        }
    }
}

/**
 * The actual implementation of PdpServiceClient so that we don't
 * have to directly export the base abstract PdpServiceClient class.
 */
class PdpServiceClientImpl extends PdpServiceClient {
    public constructor() {
        // some test will run above node env, so there is no window object
        const fetchInstance = canUseDOM() ? window.fetch.bind(window) : fetch;
        super(fetchInstance);

        if (appEnvironment.AppType === AppType.WindowsNewsPlus) {
            this.ocid = "winp2";
            this.ocidOverride = true;
        }
    }
}

/**
 * The singleton instance of PdpServiceClient for the page.
 */
export const pdpServiceClient: PdpServiceClient = new PdpServiceClientImpl();